import { IconButton } from "@/components/atoms/IconButton/IconButton";
import { t } from "@lingui/core/macro";
import { MmTooltipWrapper } from "@mm/zeus-react";
import { type KeyboardEvent, type MouseEvent, useEffect, useRef, useState } from "react";
import "./LayerExpansionPanel/style.scss";

export type LabelledLayer = {
  label?: string | null;
};

function customLayerLabel(layer: { label?: string | null }) {
  const label = layer.label?.trim();
  return label ? label : null;
}

function normalizeLayerLabel(label: string) {
  const trimmedLabel = label.trim();
  return trimmedLabel ? trimmedLabel : null;
}

const LABEL_SINGLE_CLICK_DELAY_MS = 250;

export function EditableLayerLabel({
  defaultLabel,
  layer,
  onSingleClick,
  onSave,
}: {
  defaultLabel: string;
  layer: LabelledLayer;
  onSingleClick: () => void;
  onSave: (label: string | null) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const singleClickTimeoutRef = useRef<number | null>(null);
  const customLabel = customLayerLabel(layer);
  const displayLabel = customLabel ?? defaultLabel;
  const [isEditing, setIsEditing] = useState(false);
  const [draftLabel, setDraftLabel] = useState(customLabel ?? "");

  useEffect(() => {
    if (!isEditing) {
      return;
    }
    setDraftLabel(customLabel ?? "");
    window.requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
  }, [customLabel, isEditing]);

  useEffect(() => {
    return () => {
      clearPendingSingleClick();
    };
  }, []);

  function clearPendingSingleClick() {
    if (singleClickTimeoutRef.current === null) {
      return;
    }
    window.clearTimeout(singleClickTimeoutRef.current);
    singleClickTimeoutRef.current = null;
  }

  function scheduleSingleClick() {
    clearPendingSingleClick();
    singleClickTimeoutRef.current = window.setTimeout(() => {
      singleClickTimeoutRef.current = null;
      onSingleClick();
    }, LABEL_SINGLE_CLICK_DELAY_MS);
  }

  function startEditing() {
    clearPendingSingleClick();
    setIsEditing(true);
  }

  function stopHeaderToggle(event: MouseEvent | KeyboardEvent) {
    event.stopPropagation();
  }

  function saveLabel() {
    const normalizedLabel = normalizeLayerLabel(draftLabel);
    setIsEditing(false);
    if (normalizedLabel !== customLabel) {
      onSave(normalizedLabel);
    }
  }

  function cancelEditing() {
    setDraftLabel(customLabel ?? "");
    setIsEditing(false);
  }

  if (isEditing) {
    return (
      <span
        className="editable-layer-label editable-layer-label--editing"
        onMouseDown={(event) => {
          stopHeaderToggle(event);
        }}
      >
        <input
          ref={inputRef}
          className="editable-layer-label__input"
          value={draftLabel}
          placeholder={defaultLabel}
          onChange={(event) => setDraftLabel(event.target.value)}
          onClick={stopHeaderToggle}
          onDoubleClick={stopHeaderToggle}
          onMouseDown={stopHeaderToggle}
          onBlur={saveLabel}
          onKeyDown={(event) => {
            stopHeaderToggle(event);
            if (event.key === "Enter") {
              event.currentTarget.blur();
            }
            if (event.key === "Escape") {
              cancelEditing();
            }
          }}
        />
        <IconButton
          className="editable-layer-label__cancel"
          iconName="close"
          ariaLabel={t`Cancel rename`}
          onMouseDown={(event) => {
            event.preventDefault();
            stopHeaderToggle(event);
          }}
          onClick={(event) => {
            stopHeaderToggle(event);
            cancelEditing();
          }}
        />
      </span>
    );
  }

  return (
    <span className="editable-layer-label">
      <button
        type="button"
        className="editable-layer-label__trigger"
        onClick={(event) => {
          stopHeaderToggle(event);
          if (event.detail === 1) {
            scheduleSingleClick();
            return;
          }
          clearPendingSingleClick();
        }}
        onDoubleClick={(event) => {
          stopHeaderToggle(event);
          startEditing();
        }}
        onKeyDown={(event) => {
          stopHeaderToggle(event);
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            startEditing();
          }
        }}
      >
        <span className="editable-layer-label__text">{displayLabel}</span>
      </button>
      <span className="editable-layer-label__edit-slot">
        <MmTooltipWrapper tail={true} position="top" tooltip-text={t`Rename layer`}>
          <IconButton
            className="editable-layer-label__edit"
            iconName="edit"
            ariaLabel={t`Rename layer`}
            size="medium"
            onMouseDown={(event) => {
              event.preventDefault();
              stopHeaderToggle(event);
            }}
            onClick={(event) => {
              stopHeaderToggle(event);
              startEditing();
            }}
          />
        </MmTooltipWrapper>
      </span>
    </span>
  );
}
